import {
  computed,
  effect,
  EventEmitter,
  inject,
  Injectable,
  signal
} from "@angular/core";
import { lastValueFrom } from "rxjs";
import { StudyBackend } from "src/app/modules/bootstrap/rocket-ui/backend/api/study/study.backend";
import {
  DicomService,
  DicomServiceParseResult
} from "src/app/services/dicom.service";
import { MemberStorageService } from "src/app/services/member-storage.service";
import {
  UplaodManagerStatus,
  UplaodManagerUploadStatus,
  UploadManagerService,
  UploadManagerStorageInfo,
  UploadManagerUpload
} from "src/app/services/upload-manager.service";
import { UploadProgressInfo } from "src/app/support/upload-progress";
import { uuid } from "src/app/support/uuid";

const STUDY_UPLOAD_MANAGER_STORAGE_KEY = "studyUploadManager";

export enum StudyUploadManagerUploadStatus {
  PENDING = "pending",
  QUEUED = "queued",
  UPLOADING = "uploading",
  DONE = "uploaded",
  ERROR = "error",
  IMPORTED = "imported",
  IMPORT_FAILED = "import-failed"
}

export enum StudyUploadManagerStatus {
  STANDBY = "standby",
  UPLOADING = "uploading"
}

export type StudyUploadManagerUpload = {
  id: string;
  status: StudyUploadManagerUploadStatus;
  note?: string;
  uploadManagerUploadId: string;
  dicomData?: DicomServiceParseResult;
  doImport?: boolean;
};

@Injectable()
export class StudyUploadManagerService {
  readonly #memberStorageService = inject(MemberStorageService);
  readonly #uploadManagerService = inject(UploadManagerService);
  readonly #dicomService = inject(DicomService);
  readonly #studyBackend = inject(StudyBackend);

  readonly uploadProgress = new EventEmitter<{
    studyUploadId: string;
    uploadProgress: UploadProgressInfo;
  }>();

  readonly uploadChange = new EventEmitter<StudyUploadManagerUpload>(true);

  uploads = signal<StudyUploadManagerUpload[]>(
    this.#memberStorageService
      .getItemOrDefault<StudyUploadManagerUpload[]>(
        STUDY_UPLOAD_MANAGER_STORAGE_KEY,
        []
      )
      .map((item) => {
        if (
          item.status !== StudyUploadManagerUploadStatus.DONE &&
          item.status !== StudyUploadManagerUploadStatus.IMPORTED &&
          item.status !== StudyUploadManagerUploadStatus.IMPORT_FAILED
        ) {
          item.status = StudyUploadManagerUploadStatus.PENDING;
        }

        return item;
      })
  );

  status = computed<StudyUploadManagerStatus>(() => {
    const uploadManagerStatus: UplaodManagerStatus =
      this.#uploadManagerService.status();

    switch (uploadManagerStatus) {
      case UplaodManagerStatus.STANDBY: {
        return StudyUploadManagerStatus.STANDBY;
      }
      case UplaodManagerStatus.UPLOADING: {
        return StudyUploadManagerStatus.UPLOADING;
      }
    }
  });

  constructor() {
    this.#uploadManagerService.registerImportStrategy({
      name: "droppage-study",
      strategy: async (upload: UploadManagerUpload) => {
        const studyUpload: StudyUploadManagerUpload | undefined =
          this.uploads().find((x) => x.uploadManagerUploadId === upload.id);

        if (!studyUpload) {
          return;
        }

        await lastValueFrom(
          this.#studyBackend.importFromStorage({
            version: "v1",
            body: {
              storage: {
                id: upload.file?.remoteStorageId!,
                name: upload.file?.name!
              },
              note: studyUpload.note,
              accessionNumber: studyUpload.dicomData?.accessionNumber,
              acquisitionDate: studyUpload.dicomData?.acquisitionDate,
              modality: studyUpload.dicomData?.modality,
              ownerNominative: studyUpload.dicomData?.ownerNominative,
              patientId: studyUpload.dicomData?.patientId,
              patientBreedDescription:
                studyUpload.dicomData?.patientBreedDescription,
              patientName: studyUpload.dicomData?.patientName,
              patientSpeciesDescription:
                studyUpload.dicomData?.patientSpeciesDescription,
              studyDescription: studyUpload.dicomData?.studyDescription,
              seriesDescription: studyUpload.dicomData?.seriesDescription,
              stationName: studyUpload.dicomData?.stationName
            }
          })
        );
      }
    });

    this.#uploadManagerService.uploadChange.subscribe(
      (upload: UploadManagerUpload) => {
        const studyUpload: StudyUploadManagerUpload | undefined =
          this.uploads().find((x) => x.uploadManagerUploadId === upload.id);

        if (studyUpload) {
          this.uploads.update((uploads: StudyUploadManagerUpload[]) => {
            return uploads.map((item: StudyUploadManagerUpload) => {
              if (item.uploadManagerUploadId === upload.id) {
                const updatedStatus:
                  | StudyUploadManagerUploadStatus
                  | undefined = this.#uploadStatusToSutdyUploadStatus(
                  upload.status
                );

                if (updatedStatus) {
                  const updatedItem: StudyUploadManagerUpload = {
                    ...item,
                    status: updatedStatus
                  };

                  this.uploadChange.emit(updatedItem);

                  return updatedItem;
                }

                return item;
              }

              return item;
            });
          });
        }
      }
    );

    this.#uploadManagerService.uploadProgress.subscribe(
      ({ uploadId, uploadProgress }) => {
        const studyUpload = this.uploads().find(
          (x) => x.uploadManagerUploadId === uploadId
        );

        if (studyUpload) {
          this.uploadProgress.emit({
            studyUploadId: studyUpload.id,
            uploadProgress
          });
        }
      }
    );

    effect(() => {
      const uploadsToStore = this.uploads().filter(
        (x) => x.status !== StudyUploadManagerUploadStatus.IMPORTED
      );

      this.#memberStorageService.setItem(
        STUDY_UPLOAD_MANAGER_STORAGE_KEY,
        JSON.stringify(uploadsToStore)
      );
    });
  }

  async add({
    file,
    note,
    sharings,
    doImport
  }: {
    file: File;
    doImport: boolean;
    note?: string;
    sharings?: Array<Record<string, string>>;
  }): Promise<string> {
    const dicomParseResult = await this.#dicomService.parse({ file });

    const uploadManagerUploadId: string = await this.#uploadManagerService.add({
      file,
      sharings,
      importStrategy: doImport ? "droppage-study" : undefined
    });

    const studyUpload: StudyUploadManagerUpload = {
      id: uuid(),
      status: StudyUploadManagerUploadStatus.PENDING,
      note,
      uploadManagerUploadId,
      doImport,
      dicomData: {
        accessionNumber: dicomParseResult.accessionNumber,
        stationName: dicomParseResult.stationName,
        modality: dicomParseResult.modality,
        studyInstanceUID: dicomParseResult.studyInstanceUID,
        seriesDescription: dicomParseResult.seriesDescription,
        studyDescription: dicomParseResult.studyDescription,
        acquisitionDate: dicomParseResult.acquisitionDate,
        ownerNominative: dicomParseResult.ownerNominative,
        patientId: dicomParseResult.patientId,
        patientName: dicomParseResult.patientName,
        patientSpeciesDescription: dicomParseResult.patientSpeciesDescription,
        patientBreedDescription: dicomParseResult.patientBreedDescription
      }
    };

    this.uploads.update((studyUploads: StudyUploadManagerUpload[]) => {
      return [studyUpload, ...studyUploads];
    });

    return studyUpload.id;
  }

  async remove({ id }: { id: string }): Promise<void> {
    const studyUpload: StudyUploadManagerUpload | undefined =
      this.uploads().find((upload) => upload.id === id);

    if (!studyUpload) {
      return;
    }

    this.uploads.update((studyUploads: StudyUploadManagerUpload[]) => {
      return studyUploads.filter((upload) => upload.id !== id);
    });

    await this.#uploadManagerService.remove({
      id: studyUpload.uploadManagerUploadId
    });
  }

  getDicomInfo({ id }: { id: string }): DicomServiceParseResult | undefined {
    return this.uploads().find((item) => item.id === id)?.dicomData;
  }

  getStorageInfo({ id }: { id: string }): UploadManagerStorageInfo | undefined {
    const studyUploadManagerUpload: StudyUploadManagerUpload | undefined =
      this.uploads().find((x) => x.id === id);

    if (!studyUploadManagerUpload) {
      return undefined;
    }

    return this.#uploadManagerService.getStorageInfo({
      id: studyUploadManagerUpload.uploadManagerUploadId
    });
  }

  getUploadStatus({
    id
  }: {
    id: string;
  }): StudyUploadManagerUploadStatus | undefined {
    const studyUploadManagerUpload: StudyUploadManagerUpload | undefined =
      this.uploads().find((x) => x.id === id);

    if (!studyUploadManagerUpload) {
      return undefined;
    }

    return this.#uploadStatusToSutdyUploadStatus(
      this.#uploadManagerService.getStatus({
        id: studyUploadManagerUpload.uploadManagerUploadId
      })
    );
  }

  #uploadStatusToSutdyUploadStatus(
    status: UplaodManagerUploadStatus | undefined
  ): StudyUploadManagerUploadStatus | undefined {
    switch (status) {
      case undefined:
        return undefined;
      case UplaodManagerUploadStatus.PENDING:
        return StudyUploadManagerUploadStatus.PENDING;
      case UplaodManagerUploadStatus.QUEUED:
        return StudyUploadManagerUploadStatus.QUEUED;
      case UplaodManagerUploadStatus.UPLOADING:
        return StudyUploadManagerUploadStatus.UPLOADING;
      case UplaodManagerUploadStatus.DONE:
        return StudyUploadManagerUploadStatus.DONE;
      case UplaodManagerUploadStatus.ERROR:
        return StudyUploadManagerUploadStatus.ERROR;
      case UplaodManagerUploadStatus.IMPORTED:
        return StudyUploadManagerUploadStatus.IMPORTED;
      case UplaodManagerUploadStatus.IMPORT_FAILED:
        return StudyUploadManagerUploadStatus.IMPORT_FAILED;
    }
  }
}
